import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { EventDto, EventService } from '@millionshow/api-client';
import { ModalService, Icon } from '@millionshow/client-libs';
import { CalendarEventModal } from './modals/calendar-event.modal';
import { formatDateRangeShort } from '../../utils/date-formatter';
import CalendarListComponent from './calendar-list/calendar-list.component';
import { SeoService } from '../../services/seo.service';

interface CalendarDay {
  date: Date;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventDto[];
}

@Component({
  selector: 'app-calendar-page',
  imports: [CommonModule, Icon, CalendarListComponent],
  templateUrl: './calendar.page.html',
  styleUrls: ['./calendar.page.css'],
})
export default class CalendarPage {
  private readonly eventService = inject(EventService);
  private readonly modalService = inject(ModalService);
  private readonly seoService = inject(SeoService);

  formatDateRangeShort = formatDateRangeShort;

  constructor() {
    this.seoService.set({
      title: '박람회 캘린더',
      description:
        '밀리언쇼 박람회 캘린더에서 예정 및 진행 중인 행사 일정을 확인해보세요.',
      path: '/calendar',
    });
  }

  // 현재 표시 중인 년월
  currentYear = signal<number>(new Date().getFullYear());
  currentMonth = signal<number>(new Date().getMonth());

  // 검색 쿼리
  query = signal<string>('');

  // 탭 (캘린더 or 목록)
  activeTab = signal<'calendar' | 'list'>('calendar');

  // 이벤트 데이터 로드
  $data = rxResource({
    stream: () =>
      this.eventService.eventControllerSearchV1({
        pageNo: 1,
        pageSize: 1000,
        query: this.query(),
        type: 'MILLIONSHOW' as const,
      }),
  });

  data = this.$data.value;
  items = computed(() => this.data()?.items || []);

  // 캘린더 날짜 배열 생성
  calendarDays = computed(() => {
    const year = this.currentYear();
    const month = this.currentMonth();
    const events = this.items();

    return this.generateCalendarDays(year, month, events);
  });

  // 현재 월 표시 텍스트
  currentMonthText = computed(() => {
    return `${this.currentYear()}년 ${this.currentMonth() + 1}월`;
  });

  /**
   * 캘린더 날짜 배열 생성
   */
  private generateCalendarDays(
    year: number,
    month: number,
    events: EventDto[]
  ): CalendarDay[] {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const prevLastDay = new Date(year, month, 0);

    const firstDayOfWeek = firstDay.getDay(); // 0(일) ~ 6(토)
    const lastDate = lastDay.getDate();
    const prevLastDate = prevLastDay.getDate();

    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 이전 달 날짜들
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevLastDate - i);
      days.push({
        date,
        day: prevLastDate - i,
        isCurrentMonth: false,
        isToday: false,
        events: this.getEventsForDate(date, events),
      });
    }

    // 현재 달 날짜들
    for (let i = 1; i <= lastDate; i++) {
      const date = new Date(year, month, i);
      const isToday = date.getTime() === today.getTime();
      days.push({
        date,
        day: i,
        isCurrentMonth: true,
        isToday,
        events: this.getEventsForDate(date, events),
      });
    }

    // 다음 달 날짜들 (6주 고정)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        day: i,
        isCurrentMonth: false,
        isToday: false,
        events: this.getEventsForDate(date, events),
      });
    }

    return days;
  }

  /**
   * 특정 날짜에 진행 중인 이벤트 필터링
   */
  private getEventsForDate(date: Date, events: EventDto[]): EventDto[] {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    return events.filter(event => {
      const startDate = new Date(event.startAt);
      const endDate = new Date(event.endAt);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return targetDate >= startDate && targetDate <= endDate;
    });
  }

  /**
   * 이전 달로 이동
   */
  prevMonth() {
    if (this.currentMonth() === 0) {
      this.currentYear.set(this.currentYear() - 1);
      this.currentMonth.set(11);
    } else {
      this.currentMonth.set(this.currentMonth() - 1);
    }
  }

  /**
   * 다음 달로 이동
   */
  nextMonth() {
    if (this.currentMonth() === 11) {
      this.currentYear.set(this.currentYear() + 1);
      this.currentMonth.set(0);
    } else {
      this.currentMonth.set(this.currentMonth() + 1);
    }
  }

  /**
   * 날짜 클릭 시 해당 날짜의 이벤트 모달 표시
   */
  onDateClick(day: CalendarDay) {
    if (day.events.length > 0) {
      const modal = this.modalService.create(CalendarEventModal, {
        componentProps: {
          date: day.date,
          events: day.events,
        },
      });
      if (!modal) return;
    }
  }

  /**
   * 탭 변경
   */
  changeTab(tab: 'calendar' | 'list') {
    this.activeTab.set(tab);
  }
}
